import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Checkbox as CheckBoxCommon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { AttachFile } from "@material-ui/icons";
import { Body, PrimaryButton, RegularButton, SpinnerOverlay, Spinner } from "@common";
import {
  BookingKitType,
  TimeReportTemplateType,
  useBookingKit_GetUsersLazyQuery
} from "@graphql";
import DropdownButton, {
  DropdownButtonOption
} from "@common/DropdownButton/DropdownButton";
import { OpenInNew } from "mdi-material-ui";
import { store as rootStore } from "@store";
import { UserAvatarChip } from "@containers";
import Alert from "@material-ui/lab/Alert/Alert";
import { TimereportExpenseDtoV2 } from "@api/timereports";
import { getReceiptPreviewUri } from "@bookings/BookingProfile/TimeReport/receipt-uri";
import { BookingSupportChecks } from "@bookings/BookingProfile/TimeReport/components/BookingSupportChecks";
import { environment } from "../../../../../environments/environment";
import { FormProvider } from "react-hook-form";
import { useBookingKitFormConsumer } from "./use-booking-kit-form-context";
import { ToggleButtonGroupController } from "@bonliva-crm/ui";
import Select from "@common/Select";
import { BookingWeekDocumentTypes, LegalPersonType } from "@bonliva-crm/shared";
import { resetMinValues } from "./utils";
import { compact, keyBy, uniq } from "lodash";

export type Props = {
  expenses: TimereportExpenseDtoV2[]; //todo somehow remove this dependency mb update timereportDto
  templateTypeOptions: DropdownButtonOption[];
  getSelectedTemplateTypeIndex: (value: any, options?: DropdownButtonOption[]) => number;
};

export const BookingKitForm = observer(
  ({ expenses, templateTypeOptions, getSelectedTemplateTypeIndex }: Props) => {
    const { store } = useBookingKitFormConsumer();

    const classes = useStyles({});

    const kit = store.dialog?.data;
    const form = store.form;
    const values = form.watch();
    const errors = form.formState.errors;

    const currentConfig = store.formConfigData.find((x) => x.type === values.type);
    const isRequiredPayForm = Boolean(currentConfig?.requirements?.payForm);
    const isRequiredInvoiceForm = Boolean(currentConfig?.requirements?.invoiceForm);
    const isRequiredTimeReport = Boolean(currentConfig?.requirements?.timeReport);
    const isRequiredSickPayForm = Boolean(currentConfig?.requirements?.sickPayForm);
    const isRequiredHBA = Boolean(currentConfig?.requirements?.hbaApproval);
    const isRequiredOC = Boolean(currentConfig?.requirements?.ocApproval);

    const [getUsers, { data: usersData }] = useBookingKit_GetUsersLazyQuery();
    const usersDict = keyBy(usersData?.users, (x) => x._id);

    const validateField = (
      v: any,
      key:
        | "payForm"
        | "invoiceForm"
        // | "timeReport"
        | "sickPayForm"
        | "hbaApproval"
        | "ocApproval",
      label: string
    ) => {
      if (currentConfig?.requirements) {
        if (currentConfig.requirements[key]) {
          return Boolean(v) || locale.fieldIsMandatory(label);
        }
      }
      return true;
    };

    const trDocs = useMemo(() => {
      return (
        store.data?.bookingKitDraft?.documents?.filter(
          (x) => x.documentType === BookingWeekDocumentTypes.CertifiedTimeReport
        ) ?? []
      );
    }, [store.data?.bookingKitDraft?.documents]);

    const restDocs = useMemo(() => {
      return (
        store.data?.bookingKitDraft?.documents?.filter(
          (x) => x.documentType !== BookingWeekDocumentTypes.CertifiedTimeReport
        ) ?? []
      );
    }, [store.data?.bookingKitDraft?.documents]);

    const validateTimereportField = (v: any) => {
      if (currentConfig?.requirements?.timeReport) {
        const checkedTRdoc = trDocs.some((x) => values.checkedIds[x._id] ?? false);
        return Boolean(v) || checkedTRdoc || locale.fieldIsMandatory(locale.TimeReport);
      }
      return true;
    };

    const isAnyCheckedFile = () => {
      return (
        currentConfig?.requirements &&
        Object.values(currentConfig.requirements)?.every((x) => x === false) &&
        !Object.values(values.checkedIds).any((val) => val === true)
      );
    };

    const handleSetValue = (name: any, value: any) => {
      form.setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    };

    useEffect(() => {
      if (store.dialog.open) {
        form.trigger();
      } else {
        form.reset();
      }
    }, [store.dialog.open, store.loading, values.type]);

    useEffect(() => {
      if (values.responsibleId || values.managerId) {
        getUsers({
          variables: {
            ids: compact([values.responsibleId, values.managerId])
          }
        });
      }
    }, [values.responsibleId, values.managerId]);

    const preselectDocs = (value: boolean, checkedIds: any, receipts: any) => {
      trDocs.forEach((x) => {
        checkedIds[x._id] = true;
      });

      if (restDocs.length > 0) {
        restDocs.forEach((x) => {
          checkedIds[x._id] = value;
        });
      }

      if (expenses) {
        expenses.forEach((expense) => {
          expense.attachments.forEach((attachment) => {
            receipts[attachment.uri] = { checked: value, value: attachment };
          });
        });
      }
    };

    const setDefaultValues = async (type: BookingKitType) => {
      const checkedIds = {};
      const receipts = {};
      let _values = { ...values, ...resetMinValues };

      const responsibleId =
        store.dialog?.data?.responsible?._id ||
        _values.responsibleId ||
        store.data?.contact?.ResponsiblePerson?.PersonId ||
        null;

      if (isRequiredHBA) {
        _values = {
          ..._values,
          responsibleId
        };
      } else {
        _values = { ..._values, responsibleId: null };
      }

      const managerId = await getDefaultManagerValue(responsibleId);
      _values = { ..._values, managerId: managerId };

      if (values.managerId && store.dialog?.data?._id) {
        _values = { ..._values, managerId: values.managerId };
      }

      if (trDocs.length === 0) {
        _values = { ..._values, withTimeReport: !store.isEmptyTR };
      }

      if (type === BookingKitType.SickPay) {
        preselectDocs(false, checkedIds, receipts);
        _values = { ..._values, withSickPay: hasSickPay, checkedIds, receipts };
      } else if (type === BookingKitType.Compensation) {
        preselectDocs(false, checkedIds, receipts);
        _values = { ..._values, checkedIds, receipts };
      } else if (type === BookingKitType.AccruedCost) {
        preselectDocs(true, checkedIds, receipts);
        _values = {
          ..._values,
          checkedIds,
          withSickPay: hasSickPay,
          withSubcontractorPay: hasSubcontractorForm,
          withCustomerInvoice: hasCustomerInvoice,
          withConsultantInvoice: hasConsultantInvoice,
          tags: uniq([..._values.tags, "AccruedCostSent"])
        };
      } else if (type === BookingKitType.AccruedIncome) {
        preselectDocs(true, checkedIds, receipts);
        _values = {
          ..._values,
          checkedIds,
          withSickPay: hasSickPay,
          withSubcontractorPay: hasSubcontractorForm,
          withCustomerInvoice: hasCustomerInvoice,
          withConsultantInvoice: hasConsultantInvoice,
          tags: uniq([..._values.tags, "AccruedRevenueSent"])
        };
      } else {
        preselectDocs(true, checkedIds, receipts);
        _values = {
          ..._values,
          checkedIds,
          withSickPay: hasSickPay,
          withSubcontractorPay: hasSubcontractorForm,
          withCustomerInvoice: hasCustomerInvoice,
          withConsultantInvoice: hasConsultantInvoice
        };
      }

      form.reset(_values);
    };

    const getDefaultManagerValue = async (responsibleId: string | null) => {
      let managerId = values.managerId;

      if (values.managerId && store.dialog?.data?._id) {
        managerId = values.managerId;
      } else {
        if (isRequiredOC && responsibleId) {
          const result = await store.getUserPermissions(responsibleId);

          if (result.permissions.includes("booking-kit:oc-approve:group")) {
            managerId = responsibleId;
          } else if (result.managerId) {
            managerId = result.managerId;
          }
        }
      }

      return managerId;
    };

    const setDefaultManagerValue = async (responsibleId: string | null) => {
      const managerId = await getDefaultManagerValue(responsibleId);
      form.setValue("managerId", managerId, { shouldValidate: true });
    };

    // Trigger validation when type changes. Hack to force validation of timeReportType
    useEffect(() => {
      if (!store.loading && store.dialog.open) {
        setDefaultValues(values.type);
      }
    }, [values.type, trDocs, store.loading, store.dialog.open]);

    useEffect(() => {
      if (!store.loading && store.dialog.open) {
        setDefaultManagerValue(values.responsibleId);
      }
    }, [values.responsibleId]);

    const hasConsultantInvoice = store.invoicePaymentStatuses?.paymentFormExists;
    const hasCustomerInvoice = store.invoicePaymentStatuses?.invoiceFormExists;
    const hasSickPay = store.invoicePaymentStatuses?.sickFormExists;
    const hasSubcontractorForm = store.invoicePaymentStatuses?.subcontractorFormExists;

    const timeReportItem = (
      <ListItem
        className={
          isRequiredTimeReport && form.getFieldState("withTimeReport").error
            ? classes.error
            : ""
        }
        disableGutters
        key={"timeReport"}
      >
        {!store.isEmptyTR ? (
          <>
            <CheckBoxCommon
              name={"withTimeReport"}
              onChange={(e, checked) => handleSetValue("withTimeReport", checked)}
              checked={Boolean(values?.withTimeReport)}
            />

            <div>
              <AttachFile fontSize="small" />
            </div>

            <div style={{ display: "inline-block", marginRight: 15 }}>
              {locale.TimeReport}
            </div>
            <DropdownButton
              {...form.register("timeReportType")}
              // disableBtn={!localStore.canSubmit}
              onSelect={(_, btn) =>
                handleSetValue("timeReportType", btn.value as TimeReportTemplateType)
              }
              options={templateTypeOptions}
              selected={getSelectedTemplateTypeIndex(
                values.timeReportType,
                templateTypeOptions
              )}
              withPopper
              PopperProps={{
                popperOptions: {
                  style: { zIndex: 4000 }
                }
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                cursor: "pointer"
              }}
              onClick={() =>
                window.open(
                  `/time-report/${store.bookingId}/view/${values.timeReportType}`,
                  "_blank"
                )
              }
            >
              <Body>{locale.PreviewTimeReport}</Body>
              <OpenInNew color={"primary"} />
            </div>
          </>
        ) : (
          isRequiredTimeReport &&
          errors.withTimeReport && (
            <Typography className={classes.errorText} color={"error"}>
              {errors.withTimeReport.message}
            </Typography>
          )
        )}
        <input
          {...form.register("withTimeReport", {
            validate: (v) => validateTimereportField(v)
            // value: !store.isEmptyTR && trDocs.length === 0 && true
          })}
          type={"checkbox"}
          hidden
        />
      </ListItem>
    );

    const consultantInvoiceItem = store.data?.contact?.LegalPersonType ===
      LegalPersonType.Employee && (
      <ListItem
        disableGutters
        key={"withConsultantInvoice"}
        className={isRequiredPayForm && errors.withConsultantInvoice ? classes.error : ""}
      >
        {hasConsultantInvoice ? (
          <>
            <CheckBoxCommon
              name={"withConsultantInvoice"}
              onChange={(e, checked) => handleSetValue("withConsultantInvoice", checked)}
              checked={Boolean(values?.withConsultantInvoice)}
            />

            <div>
              <AttachFile fontSize="small" />
            </div>

            <div style={{ display: "inline-block", marginRight: 15 }}>
              {locale.ConsultantInvoice}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                cursor: "pointer"
              }}
              onClick={() =>
                window.open(
                  `${environment.timereportServiceUrl}/api/internal/v1/booking/${store.bookingId}/pay-form/pdf/view`,
                  "_blank"
                )
              }
            >
              <Body>{locale.Preview}</Body>
              <OpenInNew color={"primary"} />
            </div>
          </>
        ) : (
          isRequiredPayForm &&
          errors.withConsultantInvoice && (
            <Typography className={classes.errorText} color={"error"}>
              {errors.withConsultantInvoice.message}
            </Typography>
          )
        )}
        <input
          {...form.register("withConsultantInvoice", {
            validate: (v) => validateField(v, "payForm", locale.ConsultantInvoice)
          })}
          type={"checkbox"}
          hidden
        />
      </ListItem>
    );

    const customerInvoiceItem = (
      <ListItem
        disableGutters
        key={"withCustomerInvoice"}
        className={
          isRequiredInvoiceForm && errors.withCustomerInvoice ? classes.error : ""
        }
      >
        {hasCustomerInvoice ? (
          <>
            <CheckBoxCommon
              name={"withCustomerInvoice"}
              onChange={(e, checked) => handleSetValue("withCustomerInvoice", checked)}
              checked={Boolean(values?.withCustomerInvoice)}
            />

            <div>
              <AttachFile fontSize="small" />
            </div>

            <div style={{ display: "inline-block", marginRight: 15 }}>
              {locale.CustomerInvoice}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                cursor: "pointer"
              }}
              onClick={() =>
                window.open(
                  `${environment.timereportServiceUrl}/api/internal/v1/booking/${store.bookingId}/invoice-form/pdf/view`,
                  "_blank"
                )
              }
            >
              <Body>{locale.Preview}</Body>
              <OpenInNew color={"primary"} />
            </div>
          </>
        ) : (
          isRequiredInvoiceForm &&
          errors.withCustomerInvoice && (
            <Typography className={classes.errorText} color={"error"}>
              {errors.withCustomerInvoice.message}
            </Typography>
          )
        )}
        <input
          {...form.register("withCustomerInvoice", {
            validate: (v) => validateField(v, "invoiceForm", locale.CustomerInvoice)
          })}
          type={"checkbox"}
          hidden
        />
      </ListItem>
    );

    const sickPayItem = (
      <ListItem
        disableGutters
        key={"withSickPay"}
        className={isRequiredSickPayForm && errors.withSickPay ? classes.error : ""}
      >
        {hasSickPay ? (
          <>
            <CheckBoxCommon
              name={"withSickPay"}
              onChange={(e, checked) => handleSetValue("withSickPay", checked)}
              checked={Boolean(values?.withSickPay)}
            />

            <div>
              <AttachFile fontSize="small" />
            </div>

            <div style={{ display: "inline-block", marginRight: 15 }}>
              {locale.SickForm}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                cursor: "pointer"
              }}
              onClick={() =>
                window.open(
                  `${environment.timereportServiceUrl}/api/internal/v1/booking/${store.bookingId}/sick-pay-form/pdf/view`,
                  "_blank"
                )
              }
            >
              <Body>{locale.Preview}</Body>
              <OpenInNew color={"primary"} />
            </div>
          </>
        ) : (
          isRequiredSickPayForm && (
            <Typography className={classes.errorText} color={"error"}>
              {locale.fieldIsMandatory(locale.SickForm)}
            </Typography>
          )
        )}
        <input
          {...form.register("withSickPay", {
            validate: (v) => validateField(v, "sickPayForm", locale.SickForm)
          })}
          type={"checkbox"}
          hidden
        />
      </ListItem>
    );

    const subcontractorItem = store.data?.contact?.LegalPersonType ===
      LegalPersonType.Subcontractor && (
      <ListItem
        disableGutters
        key={"withSubcontractorPay"}
        className={isRequiredPayForm && errors.withSubcontractorPay ? classes.error : ""}
      >
        {hasSubcontractorForm ? (
          <>
            <CheckBoxCommon
              name={"withSubcontractorPay"}
              onChange={(e, checked) => handleSetValue("withSubcontractorPay", checked)}
              checked={Boolean(values?.withSubcontractorPay)}
            />

            <div>
              <AttachFile fontSize="small" />
            </div>

            <div style={{ display: "inline-block", marginRight: 15 }}>
              {locale.SubcontractorPaymentForm2}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                cursor: "pointer"
              }}
              onClick={() =>
                window.open(
                  `${environment.timereportServiceUrl}/api/internal/v1/booking/${store.bookingId}/subcontractor-form/pdf/view`,
                  "_blank"
                )
              }
            >
              <Body>{locale.Preview}</Body>
              <OpenInNew color={"primary"} />
            </div>
          </>
        ) : (
          isRequiredPayForm && (
            <Typography className={classes.errorText} color={"error"}>
              {errors.withSubcontractorPay?.message}
            </Typography>
          )
        )}
        <input
          {...form.register("withSubcontractorPay", {
            validate: (v) => validateField(v, "payForm", locale.SubcontractorPaymentForm2)
          })}
          type={"checkbox"}
          hidden
        />
      </ListItem>
    );

    const expensesItems = (name: string, uri: string) => (
      <ListItem disableGutters key={uri} className={errors.receipts ? classes.error : ""}>
        <CheckBoxCommon
          {...form.register(`receipts.${uri}`, {
            value: { checked: true, value: { name, uri } }
          })}
          onChange={(e, checked) => handleReceipts(uri, checked, name)}
          checked={Boolean(values?.receipts[uri]?.checked)}
        />

        <div>
          <AttachFile fontSize="small" />
        </div>

        <div style={{ display: "inline-block", marginRight: 15 }}>{name}</div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "16px",
            cursor: "pointer"
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.open(getReceiptPreviewUri(uri), "_blank");
          }}
        >
          <Body>{locale.Preview}</Body>
          <OpenInNew color={"primary"} />
        </div>
      </ListItem>
    );

    const handleCheck = (id: string, checked: boolean) => {
      handleSetValue(`checkedIds.${id}`, checked);
      store.form.trigger("withTimeReport");
    };

    const handleReceipts = (uri: string, checked: boolean, name: string) => {
      handleSetValue(`receipts.${uri}`, { checked, value: { name, uri } });
    };

    return (
      <FormProvider {...store.form}>
        <Dialog
          fullWidth
          maxWidth="md"
          open={store.dialog.open}
          onClose={store.handleCLose}
        >
          <DialogTitle>{locale.DocumentToCertify}</DialogTitle>
          <DialogContent>
            {store.checkData && (
              <BookingSupportChecks data={store.checkData} onlyInvalid />
            )}
            {kit && (
              <div style={{ marginBottom: 10 }}>
                <Typography variant={"h5"}>
                  {`${kit.name} ${locale.bookingKitType[kit.type]}`}{" "}
                </Typography>
                <Alert severity={"error"}>
                  {locale.Rejected}
                  {` - ${locale.Comment}: ${kit.comments.join(", ")}`}
                </Alert>
              </div>
            )}

            <ToggleButtonGroupController
              {...form.register("type", { required: true })}
              name="type"
              exclusive
              label={locale.Type}
              options={Object.keys(BookingKitType).map((x) => ({
                label: locale.bookingKitType[x],
                value: x,
                disabled:
                  Boolean(store.dialog?.data?._id) ||
                  !store.formConfigData.find((c) => c.type === x)?.isAvailableForCreation
              }))}
            />

            <div
              style={{
                display: "flex",
                gap: 15,
                margin: "20px 0",
                alignItems: "flex-start"
              }}
            >
              <div>
                <Typography gutterBottom>
                  {errors.responsibleId ? (
                    <span style={{ color: "#F65629" }}>
                      {errors.responsibleId.message}
                    </span>
                  ) : (
                    locale["Responsible BA"]
                  )}
                </Typography>

                <Button
                  {...form.register("responsibleId", {
                    validate: (v) =>
                      validateField(v, "hbaApproval", locale["Responsible BA"])
                  })}
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    rootStore().ui.userSelector.open({
                      includeActiveUser: true,
                      removeDisabled: true,
                      selectedUsers: values.responsibleId ? [values.responsibleId] : [],
                      onSelect: (user) => {
                        handleSetValue("responsibleId", user._id);
                      }
                    })
                  }
                >
                  {locale.Select}
                </Button>

                {values.responsibleId && (
                  <span style={{ paddingLeft: 10 }}>
                    <UserAvatarChip
                      size="normal"
                      avatarPic={usersDict[values.responsibleId]?.AvatarPic}
                      userName={usersDict[values.responsibleId]?.Name}
                      ChipProps={{
                        onDelete: () => handleSetValue("responsibleId", null)
                      }}
                    />
                  </span>
                )}
              </div>
              <div>
                <Typography gutterBottom>
                  {errors.managerId ? (
                    <span style={{ color: "#F65629" }}>{errors.managerId.message}</span>
                  ) : (
                    locale.OCTeam
                  )}
                </Typography>

                <div
                  style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                >
                  <Button
                    {...form.register("managerId", {
                      validate: (v) => validateField(v, "ocApproval", "OC")
                    })}
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      rootStore().ui.userSelector.open({
                        includeActiveUser: true,
                        removeDisabled: true,
                        selectedUsers: values.managerId ? [values.managerId] : [],
                        onSelect: (user) => {
                          handleSetValue("managerId", user._id);
                        },
                        onlyAzureGroups: ["crm-group-oc-staffing"]
                      })
                    }
                  >
                    {locale.Select}
                  </Button>

                  {store.permissionsLoading && (
                    <div style={{ paddingLeft: 20 }}>
                      <Spinner size={16} />
                    </div>
                  )}

                  {!store.permissionsLoading && values.managerId && (
                    <span style={{ paddingLeft: 10 }}>
                      <UserAvatarChip
                        size="normal"
                        avatarPic={usersDict[values.managerId]?.AvatarPic}
                        userName={usersDict[values.managerId]?.Name}
                        ChipProps={{
                          onDelete: () => handleSetValue("managerId", null)
                        }}
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Box>
              <Select
                {...form.register("tags")}
                label={locale.ManagementStatus}
                multi
                options={store.tagsOptions ?? []}
                onChange={(v) => handleSetValue("tags", v)}
                value={values.tags}
                fullWidth
                name={`tags`}
                multiType={"chips"}
                transformValue={(v) => v.value}
              />
            </Box>

            <Box mt={1}>
              <TextField
                {...form.register("notes")}
                label={locale.Notes}
                value={values.notes}
                onChange={(e) => {
                  handleSetValue("notes", e.target.value);
                }}
                fullWidth
                multiline
              />
            </Box>

            <TextField
              {...form.register("comment")}
              label={locale.Comment}
              name="comment"
              multiline
              fullWidth
              helperText={locale["Press Enter for a new line"]}
              value={values.comment}
              onChange={(e) => handleSetValue("comment", e.target.value)}
            />

            <List
              dense
              disablePadding
              subheader={
                <ListSubheader disableGutters>
                  {errors.checkedIds || isAnyCheckedFile() ? (
                    <span style={{ color: "#F65629", fontWeight: "initial" }}>
                      {store.isEmptyTR &&
                      store.data?.bookingKitDraft?.documents.length === 0
                        ? locale.NoFiles
                        : locale.fieldIsMandatory(locale.Files)}
                    </span>
                  ) : (
                    locale.Files
                  )}
                </ListSubheader>
              }
            >
              {trDocs?.map((item) => (
                <ListItem
                  key={item._id}
                  disableGutters
                  className={errors.checkedIds ? classes.error : ""}
                >
                  <CheckBoxCommon
                    {...form.register(`checkedIds.${item._id}`)}
                    onChange={(e, checked) => handleCheck(item._id, checked)}
                    checked={Boolean(values.checkedIds[item._id])}
                  />

                  <ListItemText
                    onClick={() => window.open(`/shared-files/${item._id}`, "_blank")}
                    primary={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          // padding: "16px",
                          cursor: "pointer"
                        }}
                      >
                        <AttachFile fontSize="small" />
                        {`${locale.bookingWeekDocumentTypes[item.documentType]} - ${
                          item.name
                        }`}

                        <OpenInNew color={"primary"} />
                      </div>
                    }
                  />
                </ListItem>
              ))}
              {timeReportItem}
              {customerInvoiceItem}
              {consultantInvoiceItem}
              {sickPayItem}
              {subcontractorItem}

              {expenses &&
                expenses.map((x) => {
                  return x.attachments.map((a, i) =>
                    expensesItems(`${x.comment} ${i + 1}`, a.previewUri)
                  );
                })}
              {restDocs.map((item) => (
                <ListItem
                  key={item._id}
                  disableGutters
                  className={errors.checkedIds ? classes.error : ""}
                >
                  <CheckBoxCommon
                    {...form.register(`checkedIds.${item._id}`)}
                    onChange={(e, checked) => handleCheck(item._id, checked)}
                    checked={Boolean(values.checkedIds[item._id])}
                  />

                  <div>
                    <AttachFile fontSize="small" />
                  </div>

                  <ListItemText primary={<div>{item.name}</div>} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <RegularButton onClick={store.handleCLose}>{locale.Cancel}</RegularButton>
            {store.canDelete && (
              <RegularButton onClick={store.handleDelete}>{locale.Delete}</RegularButton>
            )}
            <PrimaryButton
              disabled={!store.form.formState.isValid || isAnyCheckedFile()}
              onClick={store.onSubmit}
            >
              {locale.Send}
            </PrimaryButton>
            <SpinnerOverlay show={store.loading} />
          </DialogActions>
          {/*<DevTool control={form.control} placement={"top-left"} />*/}
        </Dialog>
      </FormProvider>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  error: {
    "& *": {
      color: theme.palette.error.main
    }
  },
  errorText: {
    marginLeft: theme.spacing(1.5)
  }
}));
